/**
 * Phase 20.6 — Shuffle Radio Action Bar
 *
 * Mounts above the GlobalMiniPlayer whenever the current track came from
 * the shuffle radio queue (detected via `track.raw._station`). Adds
 * radio-style controls: Like, Repost, Gift, Comment, Add to Playlist,
 * Open detail page.
 *
 * - Like / Repost call existing endpoints inline (track + beat APIs).
 * - Gift / Comment / Playlist deep-link to the item's detail page where
 *   the rich UIs (GiftPicker, comment composer, playlist dialog) live.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePlayer } from "@/components/player/PlayerProvider";

const Btn = ({ onClick, title, active, children, testId, disabled }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    data-testid={testId}
    className={`h-8 w-8 rounded-full flex items-center justify-center text-base transition-colors border ${
      active
        ? "bg-pink-500/30 border-pink-300 text-pink-100"
        : "bg-white/5 border-white/10 text-gray-200 hover:bg-purple-500/20 hover:border-purple-400/50 hover:text-white"
    } disabled:opacity-40`}
  >
    {children}
  </button>
);

const ShuffleRadioActions = () => {
  const { track, isVisible } = usePlayer();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [reposted, setReposted] = useState(false);
  const [busy, setBusy] = useState(false);

  const raw = track?.raw || {};
  const isShuffle = !!raw._station;
  const isBeat = raw.kind === "beat";
  const itemId = isBeat ? raw.beat_id : raw.submission_id;

  // Reset + fetch like/repost status whenever the track changes.
  useEffect(() => {
    if (!isShuffle || !itemId) {
      setLiked(false);
      setReposted(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (isBeat) {
          const [bRes, rRes] = await Promise.all([
            api.get(`/beats/${itemId}`).catch(() => null),
            api.get(`/beats/${itemId}/repost`).catch(() => null),
          ]);
          if (cancelled) return;
          setLiked(!!bRes?.data?.beat?.is_liked_by_me);
          setReposted(!!rRes?.data?.reposted);
        } else {
          const [lRes, rRes] = await Promise.all([
            api.get(`/track/${itemId}/like-status`).catch(() => null),
            api.get(`/track/${itemId}/repost`).catch(() => null),
          ]);
          if (cancelled) return;
          setLiked(!!lRes?.data?.liked);
          setReposted(!!rRes?.data?.reposted);
        }
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [itemId, isBeat, isShuffle]);

  if (!isShuffle || !isVisible || !itemId) return null;

  const detailHref = isBeat ? `/beats/${itemId}` : `/track/${itemId}`;

  const toggleLike = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = isBeat ? `/beats/${itemId}/like` : `/track/${itemId}/like`;
      const res = await api.post(url);
      const next = isBeat ? !!res.data?.liked : !!res.data?.liked;
      setLiked(next);
      toast.success(next ? "❤️ Liked" : "Unliked");
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Sign in to like");
      else toast.error("Could not like");
    } finally { setBusy(false); }
  };

  const toggleRepost = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = isBeat ? `/beats/${itemId}/repost` : `/track/${itemId}/repost`;
      const res = await api.post(url);
      const next = !!res.data?.reposted;
      setReposted(next);
      toast.success(next ? "🔁 Reposted to RGM Shuffle" : "Repost removed");
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Sign in to repost");
      else toast.error("Could not repost");
    } finally { setBusy(false); }
  };

  const openDetail = (anchor = "") => navigate(detailHref + anchor);

  return (
    <div
      data-testid="shuffle-radio-actions"
      className="border-b border-purple-500/20 bg-black/40"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-1.5 flex items-center gap-2 overflow-x-auto">
        <span className="text-[10px] uppercase tracking-widest text-purple-300/80 font-bold whitespace-nowrap pr-1">
          🔀 Radio
        </span>
        <Btn
          onClick={toggleLike}
          title={liked ? "Unlike" : "Like"}
          active={liked}
          testId="shuffle-radio-action-like"
          disabled={busy}
        >❤</Btn>
        <Btn
          onClick={toggleRepost}
          title={reposted ? "Remove repost" : "Repost"}
          active={reposted}
          testId="shuffle-radio-action-repost"
          disabled={busy}
        >🔁</Btn>
        <Btn
          onClick={() => openDetail(isBeat ? "#gift" : "#gift")}
          title="Gift"
          testId="shuffle-radio-action-gift"
        >🎁</Btn>
        <Btn
          onClick={() => openDetail("#comments")}
          title="Comment"
          testId="shuffle-radio-action-comment"
        >💬</Btn>
        {isBeat && (
          <Btn
            onClick={() => openDetail("#playlists")}
            title="Add to playlist"
            testId="shuffle-radio-action-playlist"
          >🎼</Btn>
        )}
        <Btn
          onClick={() => openDetail("")}
          title={isBeat ? "Open beat" : "Open track"}
          testId="shuffle-radio-action-open"
        >↗</Btn>
      </div>
    </div>
  );
};

export default ShuffleRadioActions;
